import type { MouseEvent } from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faLightbulbOn } from '@fortawesome/pro-regular-svg-icons/faLightbulbOn';

import { QAIButton, QBox, QButton, QStack, THEME_ID, ThemeProvider, eclipseTheme } from '@captivateiq/quotient';

export type AILauncherDoor = {
  label: string;
  icon?: IconDefinition;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  active?: boolean;
  'aria-label'?: string;
};

type AILauncherProps = {
  onClick: (event: MouseEvent<HTMLElement>) => void;
  busy?: boolean;
  'aria-label'?: string;
  door?: AILauncherDoor;
};

const SEAM_PX = 1;
const FADE_PX = 20;

const Door = ({ label, icon = faLightbulbOn, onClick, active = false, 'aria-label': ariaLabel }: AILauncherDoor) => (
  <QButton
    aria-label={ariaLabel ?? label}
    aria-pressed={active}
    color="a"
    emphasis="high"
    size="small"
    startIcon={icon}
    sx={{
      position: 'relative',
      height: ({ spacing }) => spacing(8),
      py: 0,
      pl: 3,
      pr: '10px',
      border: 0,
      borderRadius: '4px 0 0 4px',
      bgcolor: active ? 'accent.a.borderLow' : 'accent.a.elementLow',
      color: active ? 'accent.a.actionTextMax' : 'accent.a.actionMax',
      '&:hover': { bgcolor: active ? 'accent.a.borderLow' : 'accent.a.elementMedium' },
      '&:active': { bgcolor: 'accent.a.borderLow' },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: `calc(100% + ${SEAM_PX}px)`,
        width: `${FADE_PX}px`,
        pointerEvents: 'none',
        bgcolor: 'accent.a.elementLow',
        maskImage: 'linear-gradient(90deg, #000, transparent)',
      },
    }}
    onClick={onClick}
  >
    {label}
  </QButton>
);

export const AILauncher = ({
  onClick,
  busy = false,
  'aria-label': ariaLabel = 'Ask CaptivateAI',
  door,
}: AILauncherProps) => (
  <ThemeProvider theme={{ [THEME_ID]: eclipseTheme }}>
    <QStack data-testid="ai-launcher" direction="row" sx={{ alignItems: 'center', flexShrink: 0 }}>
      {door && <Door {...door} />}
      <QBox sx={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
        <QAIButton aria-label={ariaLabel} busy={busy} size="medium" onClick={onClick} />
      </QBox>
    </QStack>
  </ThemeProvider>
);
